import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { METRIC_LABELS, METRIC_UNIT, periodLabel } from "./meta";

const VIEW_LABEL = { creator: "Criador", freelancer: "Freelancer" };

export default function GoalsModal({ open, onOpenChange, onSaved, period }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [goals, setGoals] = useState({});
  const [activeView, setActiveView] = useState(null);
  const views = Object.keys(goals);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/reports/goals", { params: period ? { period } : {} }).then((r) => {
      const g = r.data.goals || {};
      const norm = {};
      Object.entries(g).forEach(([v, d]) => {
        const t = {}; (d.available_metrics || []).forEach((m) => { t[m] = d.targets?.[m] ?? ""; });
        norm[v] = { primary: d.primary, targets: t, available: d.available_metrics || [] };
      });
      setGoals(norm);
      setActiveView(Object.keys(norm)[0] || null);
    }).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [open, period]);

  const setTarget = (v, m, val) => setGoals((p) => ({ ...p, [v]: { ...p[v], targets: { ...p[v].targets, [m]: val } } }));
  const setPrimary = (v, m) => setGoals((p) => ({ ...p, [v]: { ...p[v], primary: m } }));

  const save = async () => {
    setSaving(true);
    try {
      for (const v of views) {
        const targets = {};
        Object.entries(goals[v].targets).forEach(([m, val]) => { if (val !== "" && Number(val) > 0) targets[m] = Number(val); });
        await api.put("/reports/goals", { view: v, period, primary: goals[v].primary, targets });
      }
      toast.success("Metas salvas!");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const g = activeView ? goals[activeView] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="goals-modal" className="bg-[#121217] border-white/10 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-accent" />Minhas Metas</DialogTitle>
          <DialogDescription>Metas de <span className="text-foreground font-medium">{periodLabel(period) || "este período"}</span>. Cada período tem suas próprias metas. Marque a estrela para a meta principal.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : g ? (
          <>
            {views.length > 1 && (
              <div className="flex gap-2 shrink-0" data-testid="goals-view-tabs">
                {views.map((v) => (
                  <button key={v} data-testid={`goals-view-${v}`} onClick={() => setActiveView(v)}
                    className={`h-9 px-4 rounded-lg text-sm border transition-colors ${activeView === v ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 text-muted-foreground"}`}>
                    {VIEW_LABEL[v] || v}
                  </button>
                ))}
              </div>
            )}
            <div className="overflow-y-auto -mx-1 px-1 space-y-2 py-1">
              {g.available.map((m) => {
                const unit = METRIC_UNIT[m] || "";
                const isPrimary = g.primary === m;
                return (
                  <div key={m} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#09090B] px-3 py-2">
                    <button type="button" data-testid={`goal-primary-${m}`} onClick={() => setPrimary(activeView, m)}
                      aria-label={`Definir ${METRIC_LABELS[m] || m} como meta principal`}
                      className={`text-lg leading-none ${isPrimary ? "text-accent" : "text-white/20 hover:text-white/40"}`}>★</button>
                    <Label className="flex-1 text-sm text-foreground">{METRIC_LABELS[m] || m}</Label>
                    <div className="relative w-28">
                      {unit && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{unit}</span>}
                      <Input data-testid={`goal-input-${m}`} type="number" min="0" value={g.targets[m]}
                        onChange={(e) => setTarget(activeView, m, e.target.value)}
                        className={`h-9 bg-[#121217] border-white/10 rounded-lg ${unit ? "pl-8" : ""}`} placeholder="—" />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
        <DialogFooter>
          <Button data-testid="goals-cancel-btn" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-transparent border-white/10">Cancelar</Button>
          <Button data-testid="goals-save-btn" onClick={save} disabled={saving || loading} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-xl gap-1.5">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}Salvar metas</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
